import type maplibregl from 'maplibre-gl';

/**
 * isMapAlive — is this MapLibre instance still safe to call style-touching
 * methods on (`getLayer` / `getSource` / `removeLayer` / `setLayoutProperty` …)?
 *
 * THE BUG THIS GUARDS (P0, AK-343)
 * --------------------------------
 * MapLibre's `map.getLayer(id)` / `map.getSource(id)` are implemented as
 * `return this.style.getLayer(id)`. After `map.remove()` MapLibre nulls the
 * style (`this.style = null`) and flags `this._removed = true`. Any subsequent
 * `getLayer`/`getSource` then throws
 * `TypeError: Cannot read properties of null (reading 'getLayer')`.
 *
 * During a fast unmount/remount (e.g. navigating `/map → /team`), React runs
 * several effect cleanups: one calls `map.remove()`, and another cleanup — or
 * an async `moveend` / `requestAnimationFrame` callback still in flight — then
 * touches the map. Without this guard the TypeError bubbles to the React error
 * boundary → «Что-то пошло не так» white screen.
 *
 * Legacy `MapCanvas.tsx` (V1) already defended this exact class with per-call
 * try/catch (see its cleanup, "style = undefined → getLayer throws"). The V2
 * rewrite dropped those guards from the controller `destroy()`/`deactivate()`
 * paths and `LayerManager.destroy()`. This helper re-centralises the check so
 * every teardown can bail cleanly.
 *
 * Predicate: a map is DEAD (return false) iff it is missing, OR MapLibre's
 * `remove()` has run — which sets `_removed === true` AND nulls the style
 * (`style === null`). Both signals are checked because they are what makes
 * `getLayer`/`getSource` (`return this.style.getLayer(id)`) throw.
 *
 * We treat an *undefined* (as opposed to *null*) style as ALIVE: a real live
 * MapLibre instance always has a truthy `style`, and `remove()` sets it to
 * `null` (never `undefined`). Only an explicit `null` or the `_removed` flag
 * signals a removed map. This keeps the guard from mis-flagging minimal map
 * doubles / a pre-`load` map (which never reach these teardown paths anyway).
 */
export function isMapAlive(
  map: maplibregl.Map | null | undefined,
): map is maplibregl.Map {
  if (!map) return false;
  // `_removed` is an internal MapLibre flag set true by `remove()`. Typed as a
  // loose access because it is not on the public type surface.
  if ((map as unknown as { _removed?: boolean })._removed === true) return false;
  // `remove()` nulls the style: `this.style = null`. `getLayer`/`getSource`
  // then deref null → TypeError. An explicit null means removed → dead.
  if ((map as unknown as { style?: unknown }).style === null) return false;
  return true;
}
